"use client";

import BulkImportArticle from "../../../components/BulkImportArticle";

const data = {
  title: "Bulk Import — Opening Stock",
  audience: "For: Anyone setting starting quantities and values for existing items",
  intro: [
    "Upload the opening stock (starting quantity and value) for items that already exist, per warehouse, from an Excel/CSV-style sheet. Each row ties one item to one warehouse.",
  ],
  createsOnlyNote:
    "This tool does not create items. It only sets opening stock for items already set up. If an item doesn't exist yet, add it first (Bulk Import — Items).",
  steps: {
    commitVerb: "Apply the stock",
    commitLabel: "Apply",
    allOrNothing:
      "Applying is all-or-nothing: if even one row has a problem, nothing is recorded — the whole batch is rolled back. Check first.",
  },
  requiredFields: [
    { field: "Item No", needs: "The number of an item that already exists (exact match). Items are not created here." },
    { field: "Warehouse Name", needs: "The exact name of an existing warehouse." },
    { field: "Stock", needs: "Units in that warehouse. Up to 3 decimal places." },
    { field: "Value", needs: "Total value of that stock. Up to 3 decimal places." },
  ],
  conditionalFields: [
    { field: "Stock & Value together", rule: "Both must be filled in, or both left at zero. One without the other is rejected." },
    { field: "A 0 / 0 row", rule: "Accepted, but quietly skipped — treated as 'nothing to add here'." },
    { field: "Negative numbers", rule: "Rejected for both Stock and Value." },
    { field: "Track Inventory", rule: "The item must have Track Inventory switched on, or the row is rejected." },
  ],
  matchFields: [
    { field: "Item No", rule: "Must exactly match an existing item's number (case-sensitive)." },
    { field: "Warehouse Name", rule: "Must exactly match an existing warehouse name (case-sensitive)." },
  ],
  matchNote:
    "Item No and Warehouse Name must match existing records exactly, including capitalization.",
  duplicateRules: [
    {
      condition: "The same Item + Warehouse appears twice in your sheet",
      result: "Rejected — list each item/warehouse pairing only once per upload.",
    },
    {
      condition: "That item + warehouse already has stock on record",
      result:
        "A non-zero row is rejected. Note: this triggers if ANY stock already exists for that item+warehouse (from an earlier opening-stock upload, a purchase, production, or an adjustment) — not only from a previous opening-stock entry. A 0/0 row is always fine.",
    },
  ],
  mistakes: [
    { problem: "Item No missing or doesn't match", meaning: "The item must already exist — check the number, or add the item first." },
    { problem: "Item's Track Inventory is off", meaning: "Opening stock can only be added for inventory-tracked items." },
    { problem: "Warehouse Name missing or doesn't match", meaning: "The warehouse must already exist — check spelling." },
    { problem: "Stock and Value don't match up (one filled, one not)", meaning: "They must move together — both zero, or both greater than zero." },
    { problem: "More than 3 decimal places on Stock or Value", meaning: "Round to 3 decimals or fewer." },
    { problem: "Same item + warehouse listed twice, or stock already on record", meaning: "Each pairing once per sheet; can't re-import stock that already exists." },
  ],
  onApply: [
    "For each non-zero row, the stock and its value are recorded against that item in that warehouse.",
    "If some stock already existed for that warehouse, the new amount is added on top (not overwritten), and the average cost is recalculated to reflect both together.",
    "The accounting entry behind the scenes is updated to the new total — debiting the item's inventory account and crediting an opening-balance adjustment.",
    "The whole batch is written together in a single transaction — if anything fails, nothing is saved.",
  ],
  workflow: [
    "Build your sheet with item numbers, warehouse names, and the stock/value for each.",
    "Run the Check step first and review the problem rows.",
    "Fix each issue and re-check until every row passes.",
    "Only then run Apply to record the opening stock.",
    "Tip: if someone else imports stock for the same item and warehouse between your Check and Apply, your row can still be rejected at the last moment — just check again before retrying.",
  ],
};

export default function BulkOpeningStockPage() {
  return <BulkImportArticle data={data} />;
}
